import { useCallback, useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import BaseTable from '../../components/BaseTable/BaseTable'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import { formatDateTime } from '../../helpers/dateUtils'
import { getAutomation, getAutomationLogs } from './helper'

type Props = RouteComponentProps<{ id: string }>

const pct = (n: number) => `${Math.round((n || 0) * 100)}%`

const StatBox = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="bg-white rounded-xl shadow-md p-4 flex-1">
    <p className="text-xs font-semibold text-gray-500">{label}</p>
    <p className="text-2xl font-extrabold text-[#0B1D3A]">{value}</p>
  </div>
)

export default function AutomationLogsPage({ match }: Props) {
  const [automation, setAutomation] = useState<any>()
  const [logs, setLogs] = useState<any[]>([])
  const [stats, setStats] = useState<any>()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setLoadError(null)
    Promise.all([getAutomation(match.params.id), getAutomationLogs(match.params.id, 1, 200)])
      .then(([automationRes, logsRes]) => {
        setAutomation(automationRes.data)
        setLogs(logsRes.data?.logs || [])
        setStats(logsRes.data?.stats)
      })
      .catch((err) => setLoadError(err || 'Failed to load logs'))
      .finally(() => setLoading(false))
  }, [match.params.id])

  useEffect(() => {
    load()
  }, [load])

  const columns: any[] = [
    { Header: 'Coach', accessor: 'coachEmail' },
    { Header: 'Subject', accessor: 'subject' },
    {
      Header: 'Sent',
      accessor: 'sentAt',
      Cell: ({ value }: any) => (value ? formatDateTime(value) : '—'),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }: any) => (
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{
            background: value === 'sent' ? '#EAF7EF' : '#FDEAEA',
            color: value === 'sent' ? '#16A34A' : '#DC2626',
          }}
        >
          {value}
        </span>
      ),
    },
    {
      Header: 'Opened',
      accessor: 'openedAt',
      Cell: ({ row }: any) =>
        row.original.openedAt ? `Yes (${row.original.openCount}×) — ${formatDateTime(row.original.openedAt)}` : 'No',
    },
    {
      Header: 'Clicked',
      accessor: 'clickedAt',
      Cell: ({ row }: any) =>
        row.original.clickedAt ? `Yes (${row.original.clickCount}×) — ${formatDateTime(row.original.clickedAt)}` : 'No',
    },
    { Header: 'Error', accessor: 'errorMessage' },
  ]

  return (
    <DashboardWrapper page="subscriptions-automation" title={automation ? `Logs — ${automation.name}` : 'Automation Logs'}>
      {loadError && (
        <div className="bg-white border border-red-200 rounded-xl p-4 text-sm text-red-600 mb-2">
          Couldn't load: {loadError}{' '}
          <button type="button" className="underline" onClick={load}>
            Retry
          </button>
        </div>
      )}
      {stats && (
        <div className="flex gap-4 mb-4">
          <StatBox label="Sent" value={stats.sent} />
          <StatBox label="Opened" value={`${stats.opened} (${pct(stats.openRate)})`} />
          <StatBox label="Clicked" value={`${stats.clicked} (${pct(stats.clickRate)})`} />
        </div>
      )}
      <BaseTable data={logs} columns={columns} loading={loading} />
    </DashboardWrapper>
  )
}
